import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { Card, Form, Input, Button, Checkbox, Typography } from "antd";
import { UserOutlined, LockOutlined, LoginOutlined } from "@ant-design/icons";
import { getAuthToken, isTokenExpired, getUserType, logout } from "../../utility/auth";
import logo from "../../assets/logo.png";
import ChangelogModal from "../../components/ChangelogModal";
import { APP_VERSION } from "../../version";

const { Title, Text } = Typography;

export default function Login() {
    const [loading, setLoading] = useState(false);
    const [showChangelog, setShowChangelog] = useState(false);
    const navigate = useNavigate();

    const handleLogin = ({ uname, pass, keepLoggedIn }) => {
        setLoading(true);
        const empdata = { uname, pass };

        axios.post("/login", empdata)
            .then((res) => {
                if (res.status === 200) {
                    const data = res.data;
                    if (data.token) {
                        const token = data.token;
                        const usertype = data.data.type.toLowerCase(); // Normalize to lowercase
                        const expirationTime = Date.now() + 3 * 60 * 60 * 1000; // 3 hours

                        if (keepLoggedIn) {
                            localStorage.setItem("token", token);
                            localStorage.setItem("expirationTime", expirationTime);
                            localStorage.setItem("uname", data.data.uname);
                            localStorage.setItem("usertype", usertype); // Store normalized usertype
                        } else {
                            sessionStorage.setItem("token", token);
                            sessionStorage.setItem("expirationTime", expirationTime);
                            sessionStorage.setItem("uname", data.data.uname);
                            sessionStorage.setItem("usertype", usertype); // Store normalized usertype
                        }

                        toast.success("Logged in Successfully");

                        // Add delay before redirect
                        setTimeout(() => {
                            if (usertype === "admin") {
                                navigate("/dashboard/analytics", { replace: true });
                            } else if (usertype === "account") {
                                navigate("/dashboard/account", { replace: true });
                            } else if (usertype === "cashier") {
                                navigate("/dashboard/cashier", { replace: true }); // Use existing cashier dashboard
                            } else if (usertype === "manager") {
                                navigate("/dashboard/analytics", { replace: true }); // Redirect managers to analytics dashboard
                            } else {
                                console.warn("Unknown user type:", usertype);
                                navigate("/dashboard/analytics", { replace: true }); // Default fallback to admin dashboard
                            }
                        }, 1000); // Increased delay

                    } else {
                        toast.error("Invalid Token Received");
                    }
                } else if (res.status === 401) {
                    toast.error("Wrong username/Password");
                }
            })
            .catch((err) => {
                toast.error("Wrong username/Password");
                console.log(err.message);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        const token = getAuthToken();
        const userType = getUserType();

        if (token && !isTokenExpired()) {
            const normalizedUserType = userType?.toLowerCase();

            switch (normalizedUserType) {
                case "admin":
                    navigate("/dashboard/admin", { replace: true });
                    break;
                case "account":
                    navigate("/dashboard/account", { replace: true });
                    break;
                case "cashier":
                    navigate("/dashboard/cashier", { replace: true }); // Use existing cashier dashboard
                    break;
                case "manager":
                    navigate("/dashboard/admin", { replace: true }); // Redirect managers to admin dashboard
                    break;
                default:
                    console.warn("Unknown user type, staying on login");
                    logout(); // Clear invalid session
                    break;
            }
        }
    }, [navigate]);

    return (
        <>
            <ToastContainer />
            <div style={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #0f172a 0%, #0d9488 100%)",
                padding: 16,
            }}>
                <Card
                    style={{ width: 380, borderRadius: 14 }}
                    styles={{ body: { padding: "32px 28px" } }}
                >
                    <div style={{ textAlign: "center", marginBottom: 24 }}>
                        <img src={logo} alt="CloudNet Softwares" style={{ height: 40, marginBottom: 12 }} />
                        <Title level={4} style={{ margin: 0 }}>StoreHub</Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>Retail Management System</Text>
                    </div>

                    <Form layout="vertical" onFinish={handleLogin} initialValues={{ keepLoggedIn: false }}>
                        <Form.Item
                            name="uname"
                            label="Username"
                            rules={[{ required: true, message: "Please enter your username" }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder="Enter your username" autoComplete="username" />
                        </Form.Item>

                        <Form.Item
                            name="pass"
                            label={
                                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                    <span>Password</span>
                                    <Link to="/forgot-password" style={{ fontSize: 12 }}>Forgot password?</Link>
                                </div>
                            }
                            rules={[{ required: true, message: "Please enter your password" }]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" autoComplete="current-password" />
                        </Form.Item>

                        <Form.Item name="keepLoggedIn" valuePropName="checked" style={{ marginBottom: 16 }}>
                            <Checkbox>Keep me logged in</Checkbox>
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button type="primary" htmlType="submit" icon={<LoginOutlined />} loading={loading} block size="large">
                                Sign In to StoreHub
                            </Button>
                        </Form.Item>
                    </Form>

                    <div style={{ textAlign: "center", marginTop: 20 }}>
                        <Text
                            type="secondary"
                            onClick={() => setShowChangelog(true)}
                            style={{ fontSize: 12, cursor: "pointer" }}
                            title="View changelog"
                        >
                            Version {APP_VERSION}
                        </Text>
                    </div>
                </Card>
            </div>

            <ChangelogModal open={showChangelog} onClose={() => setShowChangelog(false)} />
        </>
    );
}
